import { withMixpanel } from "./events/mixpanelClient";

export const UNTRADEABLE_ONLY = "untradeable_only";

export const INCLUDE_ACTIVE_SQUAD = "include_active_squad";
export const INCLUDE_SBC_PLAYERS = "include_sbc_players";
export const INCLUDE_OBJECTIVE_PLAYERS = "include_objective_players";

export const PRIORITIZE_DUPLICATES = "prioritize_duplicates";
export const PRIORITIZE_SBC_STORAGE = "prioritize_sbc_storage";
export const PRIORITIZE_TRANSFERLIST = "prioritize_transferlist";

export const INCLUDED_PLAYERS = "included_players";
export const EXCLUDED_PLAYERS = "excluded_players";

export const SBC_PARAM_KEYS = [
  UNTRADEABLE_ONLY,
  INCLUDE_ACTIVE_SQUAD,
  INCLUDE_SBC_PLAYERS,
  INCLUDE_OBJECTIVE_PLAYERS,
  PRIORITIZE_DUPLICATES,
  PRIORITIZE_SBC_STORAGE,
  PRIORITIZE_TRANSFERLIST,
  INCLUDED_PLAYERS,
  EXCLUDED_PLAYERS,
];

export const ELITE_SUBSCRIPTION = "elite";
export const PRO_SUBSCRIPTION = "pro";
export const ULTIMATE_SUBSCRIPTION = "ultimate";

export const ELITE_YEARLY_SUBSCRIPTION = "elite_yearly";
export const PRO_YEARLY_SUBSCRIPTION = "pro_yearly";
export const ULTIMATE_YEARLY_SUBSCRIPTION = "ultimate_yearly";

export const EXCLUDE_PAID_EVOLUTIONS = "exclude_paid_evolutions";

export const EXCLUDE_COMPLETED_EVOLUTIONS = "exclude_completed_evolutions";

/** When "true", skip the reset-to-default confirmation modal in evolution filters */
export const SKIP_RESET_EVOLUTIONS_DEFAULT_MODAL =
  "skip_reset_evolutions_default_modal";

/** When "true", skip the "Mark as Planned Evolution" confirmation popup */
export const SKIP_MARK_AS_PLANNED_MODAL = "skip_mark_as_planned_modal";

export const IOS_CLUB_IMPORT_APP_URL =
  "https://apps.apple.com/us/app/club-import/id6737269250";

// The EasySBC app's Play listing (package io.easysbc). The UTM tags give the
// web its own row in Play Console's store listing acquisition report.
export const ANDROID_APP_URL =
  "https://play.google.com/store/apps/details?id=io.easysbc&utm_source=easysbc&utm_campaign=android_beta";

const CHROME_EXTENSION_LISTING_URL =
  "https://chromewebstore.google.com/detail/easysbc-club-import/mchecdiinfipdfihkoebfbpfnllbllhc";

// UTM tags give each placement its own row in the Web Store's page views by source
export const chromeExtensionUrl = (
  placement: "import_tutorial" | "troubleshoot" | "review_prompt",
  path = "",
) =>
  `${CHROME_EXTENSION_LISTING_URL}${path}?utm_source=easysbc&utm_medium=${placement}&utm_campaign=club_import`;

export const EA_WEB_APP_URL =
  "https://www.ea.com/ea-sports-fc/ultimate-team/web-app/";

export const BREAKPOINTS = {
  MEDIUM_MOBILE: {
    value: 375,
    query: "(max-width: 375px)",
  },
  MEDIUM2_MOBILE: {
    value: 530,
    query: "(max-width: 530px)",
  },
  MOBILE: {
    value: 768,
    query: "(max-width: 768px)",
  },
  TOUCH_DEVICE: {
    value: 1024,
    query: "(max-width: 1025px)",
  },
  SMALL_DESKTOP: {
    value: 1280,
    query: "(max-width: 1279px)",
  },
  MAX_CONTENT: {
    value: 1536,
    query: "(max-width: 1536px)",
  },
};

export const FEATUREBASE_SSO_BASE_URL =
  "https://easysbc.featurebase.app/api/v1/auth/access/jwt";
export const FEEDBACK_PORTAL_RETURN_URL = "https://feedback.easysbc.io";

export const CHURNKEY_APP_ID = "sanfl9jbj";

export const PAST_DUE_GROUP_ID = "past_due_experiment_group";

export const GROUP_ID = "price_format_experiment_rounded_or_exact";

// Generic client-side A/B test bucketing helpers.
// Specific tests (their group IDs, weights and getters) live under
// src/experiments/ — e.g. experiments/clientExperiments.ts for the front-page
// feed-order test. These helpers stay generic and reusable.
// Use for tests where bucketing happens client-side (typically on click)
// with no pre-click UI variation. Each user is stickily assigned via
// localStorage. The bucket can optionally be registered as a Mixpanel
// super property so downstream events inherit it.
// For server-driven tests where the backend assigns a group and stores
// it on `user.experimentGroups`, see the example block above.

const safeGetItem = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSetItem = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // private browsing, quota exceeded, storage disabled
  }
};

export const pickRandomTestGroup = <T extends string>(
  groups: readonly T[],
): T => {
  return groups[Math.floor(Math.random() * groups.length)] as T;
};

/**
 * Returns the user's sticky bucket for a client-side A/B test.
 * - Reads the bucket from localStorage and validates it against the
 *   provided allow-list of group names.
 * - If missing or invalid (e.g. a stale variant from an old experiment),
 *   randomly assigns a new bucket and persists it.
 * - If `mixpanelProperty` is provided, registers the bucket as a
 *   Mixpanel super property so downstream events carry it.
 */
export const getOrAssignTestGroup = <T extends string>(
  storageKey: string,
  groups: readonly T[],
  options?: { mixpanelProperty?: string },
): T => {
  const stored = safeGetItem(storageKey);
  let group: T;
  if (stored !== null && (groups as readonly string[]).includes(stored)) {
    group = stored as T;
  } else {
    group = pickRandomTestGroup(groups);
    safeSetItem(storageKey, group);
  }
  if (options?.mixpanelProperty) {
    withMixpanel((mp) => mp.register({ [options.mixpanelProperty]: group }));
  }
  return group;
};

type WeightedGroup<T extends string> = { value: T; weight: number };

/**
 * Weighted random pick using cumulative weights. Weights are relative — they
 * need not sum to 100 (e.g. 10/45/45).
 */
export const pickWeightedTestGroup = <T extends string>(
  groups: readonly WeightedGroup<T>[],
): T => {
  const total = groups.reduce((sum, g) => sum + g.weight, 0);
  let roll = Math.random() * total;
  for (const g of groups) {
    roll -= g.weight;
    if (roll < 0) return g.value;
  }
  // Floating-point safety net: fall back to the last group.
  return groups[groups.length - 1].value;
};

/**
 * Weighted sibling of getOrAssignTestGroup. PURE — localStorage only, no
 * mixpanel — so it is safe to call from any render/loader before mixpanel has
 * finished initializing. Registering the bucket as a super property is done
 * separately, once, after mixpanel.init (see index.tsx). Returns the sticky
 * bucket plus `isNew` (true only when this call freshly assigned the group).
 */
export const getOrAssignWeightedTestGroup = <T extends string>(
  storageKey: string,
  groups: readonly WeightedGroup<T>[],
): { group: T; isNew: boolean } => {
  const allowed = groups.map((g) => g.value) as readonly string[];
  const stored = safeGetItem(storageKey);
  if (stored !== null && allowed.includes(stored)) {
    return { group: stored as T, isNew: false };
  }
  const group = pickWeightedTestGroup(groups);
  safeSetItem(storageKey, group);
  return { group, isNew: true };
};
